import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowDown,
  ArrowDownLeft,
  ArrowRight,
  ArrowUpRight,
  Boxes,
  CalendarClock,
  CircleAlert,
  Receipt,
  RefreshCw,
  LayoutDashboard,
  PackageCheck,
  ShoppingCart,
  TrendingUp,
  TriangleAlert,
  Warehouse,
  Waves,
  X,
} from 'lucide-react';
import {
  atualizarEstoque,
  atualizarVendas,
  useDashboard,
  useFaturamentoPorHora,
  useOcupacaoEmpresa,
  usePainelPedidos,
  useSerieEmpresa,
} from '@/hooks/consultas';
import { useContexto } from '@/hooks/useContexto';
import { CLASSE_SEVERIDADE, derivarAlertas } from '@/lib/alertas';
import { resumirOcupacao } from '@/lib/ocupacao';
import { cn, dataCurta, moeda, moedaCompacta, numero, percentual } from '@/lib/utils';
import type {
  CanalMarketplace,
  DiaAgendado,
  ReferenciaDoDia,
  FaturamentoPorHoraResposta,
  OcupacaoEmpresaResposta,
  PainelPedidosResposta,
  PontoSerieEmpresa,
} from '@/lib/tipos';
import {
  CabecalhoCartao,
  CabecalhoPagina,
  CorpoCartao,
  Cartao,
  Esqueleto,
  Kpi,
  Pilula,
  TituloCartao,
} from '@/componentes/ui/primitivos';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Botao } from '@/componentes/ui/botao';

export function Inicio(): JSX.Element {
  const { centro, centroAtual, empresa } = useContexto();
  const cliente = useQueryClient();
  const consulta = useDashboard(centro);

  /*
    A seleção de estoques vive AQUI, e não dentro do card, porque o gráfico de
    30 dias e o card de consolidação têm de somar exatamente os mesmos centros.
    Com o estado dentro do card, desmarcar um estoque mudava o total de hoje e
    deixava a curva falando de outro conjunto — dois números concorrentes na
    mesma tela, que é o problema que este card veio resolver.

    `null` = ainda não chegou a resposta; o padrão do cadastro é aplicado uma
    vez, quando ela chega.
  */
  const consultaEmpresa = useOcupacaoEmpresa(empresa);
  const dadosEmpresa = consultaEmpresa.data;
  const [marcados, definirMarcados] = React.useState<Set<number> | null>(null);

  React.useEffect(() => {
    if (!dadosEmpresa) return;
    definirMarcados(
      new Set(
        dadosEmpresa.centros
          .filter((c) => c.contabiliza && c.dataBase !== null)
          .map((c) => c.centroEstoque),
      ),
    );
  }, [dadosEmpresa]);

  const selecao = React.useMemo(() => marcados ?? new Set<number>(), [marcados]);

  if (consulta.isLoading || centro === null) return <EsqueletoInicio />;
  if (consulta.isError) {
    return (
      <div className="p-4">
        <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
      </div>
    );
  }

  const dados = consulta.data;
  if (!dados) return <EsqueletoInicio />;

  const resumo = dados.resumo;
  const alertas = derivarAlertas(dados, centro);
  const base = `/estoque?centro=${centro}`;

  const indisponiveis = resumo
    ? resumo.posicoesTotal - resumo.posicoesOcupadas - resumo.posicoesLivres
    : 0;

  return (
    <div className="space-y-4 p-3 sm:p-5">
      <CabecalhoPagina
        icone={<LayoutDashboard className="h-5 w-5" />}
        sobrelinha={centroAtual ? `${centroAtual.centroEstoque} · ${centroAtual.nome}` : 'Centro'}
        titulo="Como está o CD agora"
        acoes={
          <Pilula tom="neutro">posições até {dataCurta(dados.dataBase)}</Pilula>
        }
      />

      {!resumo ? (
        <Cartao>
          <EstadoVazio
            titulo="Este centro ainda não tem fechamento de estoque"
            detalhe="Sem posição de fechamento não há ocupação para calcular. Escolha outro centro no cabeçalho."
          />
        </Cartao>
      ) : (
        <>
          {/* A outra metade da fronteira: as duas seções se anunciam do mesmo
              jeito, senão só a de baixo pareceria "a parte diferente". */}
          <FaixaSecao
            icone={<Warehouse className="h-[18px] w-[18px]" />}
            titulo="Estoque"
            detalhe={`Posição e movimento do centro ${centro}`}
            acao={
              <BotaoAtualizar
                aoAtualizar={() => atualizarEstoque(cliente, centro, empresa)}
              />
            }
          />

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <LinkKpi destino={base}>
              <Kpi
                rotulo="Ocupação"
                valor={percentual(resumo.percentualOcupacao)}
                /*
                  "no centro X" pelo mesmo motivo do KPI ao lado: a
                  consolidação da empresa está na mesma tela, e dois números
                  de ocupação sem dono é como alguém leva o do CD para uma
                  reunião achando que é o da empresa.
                */
                nota={`${numero(resumo.posicoesOcupadas)} de ${numero(
                  resumo.posicoesTotal,
                )} posições no centro ${centro}`}
                barra={resumo.percentualOcupacao}
                icone={<Boxes className="h-4 w-4" />}
                tom={resumo.percentualOcupacao >= 90 ? 'alerta' : 'marca'}
                className="h-full"
              />
            </LinkKpi>

            <Kpi
              rotulo="Posições livres"
              /*
                O centro vai no rótulo do número, e não só no cabeçalho da
                página: logo abaixo há o total da EMPRESA, e dois números
                grandes de ocupação na mesma tela sem dizer de quem são é
                pedir para alguém levar o do CD achando que é o da empresa.
              */
              valor={numero(resumo.posicoesLivres)}
              nota={`prontas para alocar no centro ${centro}`}
              icone={<PackageCheck className="h-4 w-4" />}
              tom="ok"
            >
              <div className="mt-3 space-y-1 border-t border-borda pt-2.5 text-xs">
                <LinhaAtalho
                  para={`${base}&status=ocupada`}
                  rotulo="ocupadas"
                  valor={numero(resumo.posicoesOcupadas)}
                />
                <LinhaAtalho
                  para={`${base}&status=indisponivel`}
                  rotulo="indisponíveis"
                  valor={numero(indisponiveis)}
                />
                {/* Âncora para a consolidação, que está no fim da página.
                    Quem pergunta "e os outros estoques?" está olhando ESTE
                    número — o caminho tem de sair daqui, não do menu. */}
                <a
                  href="#ocupacao-empresa"
                  className="group flex items-center justify-between rounded-md px-1 py-0.5 text-primaria transition-colors hover:bg-primaria-suave"
                >
                  <span>ver a ocupação da empresa</span>
                  <ArrowDown className="h-3.5 w-3.5 transition-transform group-hover:translate-y-0.5" />
                </a>
              </div>
            </Kpi>

            <Kpi
              rotulo="Movimentações hoje"
              valor={numero(dados.movimentacoesHoje.total)}
              nota="itens movimentados"
              icone={<TrendingUp className="h-4 w-4" />}
              tom="neutro"
            >
              <div className="mt-3 flex gap-2 border-t border-borda pt-2.5">
                <MiniFluxo
                  icone={<ArrowDownLeft className="h-3.5 w-3.5" />}
                  valor={numero(dados.movimentacoesHoje.entradas)}
                  rotulo="entradas"
                  classe="text-status-livre bg-status-livreFundo"
                />
                <MiniFluxo
                  icone={<ArrowUpRight className="h-3.5 w-3.5" />}
                  valor={numero(dados.movimentacoesHoje.saidas)}
                  rotulo="saídas"
                  classe="text-status-divergencia bg-status-divergenciaFundo"
                />
              </div>
            </Kpi>

            <Kpi
              rotulo="Precisa de atenção"
              valor={numero(alertas.length)}
              nota={alertas.length === 1 ? 'ponto de atenção' : 'pontos de atenção'}
              icone={<TriangleAlert className="h-4 w-4" />}
              tom={alertas.length > 0 ? 'erro' : 'ok'}
            >
              <div className="mt-3 space-y-1 border-t border-borda pt-2.5 text-xs">
                <LinhaAtalho
                  para={`/movimentos/negativos?centro=${centro}`}
                  rotulo="saldo negativo"
                  valor={numero(resumo.posicoesComSaldoNegativo)}
                  alerta={resumo.posicoesComSaldoNegativo > 0}
                />
                <LinhaAtalho
                  para={`/analises?centro=${centro}&p=sem-endereco`}
                  rotulo={resumo.estoqueSemEndereco < 0 ? 'fora de endereço' : 'sem endereço'}
                  valor={numero(resumo.estoqueSemEndereco)}
                  alerta={resumo.estoqueSemEndereco < 0}
                />
              </div>
            </Kpi>
          </div>

          {/*
            A EMPRESA VEM LOGO DEPOIS DOS NÚMEROS DO CENTRO.

            Estava no pé da seção, depois do gráfico de 30 dias — e o atalho
            "ver a ocupação da empresa", que sai do KPI acima, jogava a pessoa
            para o fim da página. O total da empresa é a leitura seguinte à do
            centro, não um apêndice: quem acabou de ler "70,8% no 3201" quer
            saber o da empresa na linha de baixo, não três cartões adiante.
          */}
          {empresa === null ? null : (
            <OcupacaoDaEmpresa consulta={consultaEmpresa} selecao={selecao} />
          )}

          <div className="grid gap-3 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
            {empresa === null ? null : <SerieOcupacao empresa={empresa} />}
            <PrecisaDeAtencao alertas={alertas} />
          </div>

          {/*
            A FRONTEIRA ENTRE DOIS ASSUNTOS, e ela precisa ser visível.

            Daqui para baixo nada fala de posição, palete ou ocupação: fala de
            PEDIDO. São duas perguntas que a mesma pessoa faz na mesma tela e
            que não se somam — "o galpão está cheio?" e "a venda está andando?".
            Sem a faixa, o primeiro cartão de pedidos encosta no último de
            estoque e os dois viram um bloco só; foi por isso que a régua e o
            rótulo entraram, e não por decoração.
          */}
          {empresa === null ? null : <VendasDoDia empresa={empresa} />}
        </>
      )}
    </div>
  );
}

/**
 * O botão "Atualizar" de uma seção.
 *
 * UM POR SEÇÃO, e não um por cartão. Os três cartões de venda saem da MESMA
 * consulta (`/api/pedidos/painel`): três botões ali seriam três botões fazendo
 * exatamente a mesma coisa, e quem apertasse um veria os outros dois mudarem
 * junto sem entender por quê. Do outro lado, um botão único para a página
 * inteira refaria a série de 30 dias — 3,5 s — toda vez que alguém só quisesse
 * o faturamento da hora.
 *
 * A seção é onde as consultas de fato se separam, então é onde o botão cabe.
 */
function BotaoAtualizar({ aoAtualizar }: { aoAtualizar: () => Promise<void> }): JSX.Element {
  const [rodando, definirRodando] = React.useState(false);

  return (
    <Botao
      variante="secundaria"
      tamanho="pequeno"
      carregando={rodando}
      onClick={() => {
        if (rodando) return;
        definirRodando(true);
        /* O `finally` também roda no erro: botão que fica girando para sempre
           depois de uma falha de rede é pior que o erro em si. */
        void aoAtualizar().finally(() => definirRodando(false));
      }}
    >
      <RefreshCw className="h-3.5 w-3.5" />
      Atualizar
    </Botao>
  );
}

/**
 * Faixa de seção — o que separa estoque de vendas nesta tela.
 *
 * Uma régua sozinha não basta: ela diz "mudou de coisa" sem dizer para qual.
 * Com o rótulo e o ícone, quem chega rolando a página sabe em que assunto está
 * antes de ler o primeiro número — que é o que evita levar um total de pedidos
 * para uma conversa sobre ocupação.
 */
function FaixaSecao({
  icone,
  titulo,
  detalhe,
  acao,
}: {
  icone: React.ReactNode;
  titulo: string;
  detalhe?: string | undefined;
  acao?: React.ReactNode | undefined;
}): JSX.Element {
  return (
    <div className="flex items-center gap-3 pt-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-superficie2 text-primaria ring-1 ring-inset ring-borda">
        {icone}
      </span>
      <span className="min-w-0 leading-tight">
        <span className="block text-[15px] font-bold tracking-[-0.01em]">{titulo}</span>
        {detalhe ? <span className="block text-[12px] text-textoFraco">{detalhe}</span> : null}
      </span>
      <span aria-hidden className="ml-1 h-px flex-1 bg-borda" />
      {acao}
    </div>
  );
}

/** Regra 4: o número é o link. Um KPI que não abre a origem não entra. */
function LinkKpi({
  destino,
  children,
}: {
  destino: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Link
      to={destino}
      className="block h-full rounded-xl transition-transform hover:-translate-y-0.5 focus-visible:-translate-y-0.5"
    >
      {children}
    </Link>
  );
}

function LinhaAtalho({
  para,
  rotulo,
  valor,
  alerta = false,
}: {
  para: string;
  rotulo: string;
  valor: string;
  alerta?: boolean;
}): JSX.Element {
  return (
    <Link
      to={para}
      className="group flex items-center justify-between rounded-md px-1 py-0.5 text-textoFraco transition-colors hover:bg-superficie2 hover:text-texto"
    >
      <span>{rotulo}</span>
      <span
        className={cn(
          'tabular font-semibold',
          alerta ? 'text-status-divergencia' : 'text-texto',
        )}
      >
        {valor}
      </span>
    </Link>
  );
}

function MiniFluxo({
  icone,
  valor,
  rotulo,
  classe,
}: {
  icone: React.ReactNode;
  valor: string;
  rotulo: string;
  classe: string;
}): JSX.Element {
  return (
    <span className={cn('flex flex-1 items-center gap-1.5 rounded-lg px-2 py-1.5', classe)}>
      {icone}
      <span className="leading-tight">
        <span className="tabular block text-[13px] font-bold">{valor}</span>
        <span className="block text-[10px] opacity-70">{rotulo}</span>
      </span>
    </span>
  );
}

/**
 * Série de 30 dias — da EMPRESA, nos estoques marcados no card abaixo.
 *
 * Era do centro sozinho. Virou da empresa porque a pergunta que se faz olhando
 * uma curva de ocupação é "o galpão está enchendo?", e galpão não tem centro:
 * a mesma rua guarda venda, open box e reserva. Cada ponto conta ENDEREÇO
 * distinto, a mesma métrica do card de hoje — a curva e o número de hoje
 * passam a ser a mesma coisa medida em dois momentos.
 *
 * Mostra o CONTADOR junto do percentual. Só o % esconde o que mudou: 45% de
 * 12.300 endereços e 45% de 12.700 são a mesma barra e 400 endereços de
 * diferença — e o denominador cresce de verdade quando cadastram rua nova.
 */
/**
 * A curva de 30 dias da empresa.
 *
 * NÃO RECEBE MAIS A LISTA DE CENTROS, e isso é uma correção de tempo de
 * abertura. Ela recebia a seleção do cartão de ocupação, que só existe depois
 * que `/api/empresa/ocupacao` responde — então a série (836 ms) só COMEÇAVA
 * 536 ms depois do carregamento, em cascata, e sozinha respondia por 1,4 s dos
 * 2 s da tela.
 *
 * A dependência deixou de fazer sentido quando a lista de estoques saiu do
 * cartão: sem checkbox, a seleção é sempre o padrão do cadastro — que é
 * exatamente o que a API já usa quando não se manda centro nenhum. Sem o
 * parâmetro, a consulta dispara junto com todas as outras.
 */
function SerieOcupacao({ empresa }: { empresa: number }): JSX.Element {
  const consulta = useSerieEmpresa(empresa);
  const serie = consulta.data?.serie ?? [];
  /* Quantos estoques a API somou — vindo da resposta, não de um palpite da
     tela. É o número que descreve a curva que está desenhada. */
  const somados = consulta.data?.centros.length ?? 0;

  if (consulta.isLoading) {
    return (
      <Cartao className="flex flex-col">
        <CabecalhoCartao>
          <TituloCartao>Ocupação da empresa nos últimos 30 dias</TituloCartao>
        </CabecalhoCartao>
        <div className="p-4">
          <Esqueleto className="h-[228px] rounded-lg" />
        </div>
      </Cartao>
    );
  }

  if (serie.length < 2) {
    return (
      <Cartao>
        <CabecalhoCartao>
          <TituloCartao>Ocupação da empresa nos últimos 30 dias</TituloCartao>
        </CabecalhoCartao>
        {/* "Nenhum estoque marcado" saiu junto com os checkboxes: não há mais o
            que marcar, então o único caso restante é não haver fechamento. */}
        <EstadoVazio
          titulo="Série de 30 dias indisponível"
          detalhe="Os estoques desta empresa não têm dois dias de fechamento para comparar."
        />
      </Cartao>
    );
  }

  const ultimo = serie[serie.length - 1]!;
  const primeiro = serie[0]!;
  const delta = ultimo.percentual - primeiro.percentual;
  const deltaEnderecos = ultimo.ocupados - primeiro.ocupados;
  /*
    A CURVA É DE ENDEREÇOS OCUPADOS, e não do percentual.

    O percentual tem denominador móvel: um centro que entra ou sai da conta
    muda a linha sem que um único palete tenha se mexido. Quem olha este cartão
    quer saber se o galpão encheu, e para isso a resposta honesta é a contagem.
    O percentual não sumiu — continua na pílula e no tooltip, onde é
    comparação, não a série.

    Por isso o pico também passa a ser o de CONTAGEM: com o denominador
    variando, o maior percentual e a maior ocupação podem cair em dias
    diferentes, e mostrar um pico que não é o da curva desenhada confunde mais
    do que informa.
  */
  const pico = serie.reduce((m, p) => (p.ocupados > m.ocupados ? p : m), serie[0]!);
  const ocupados = serie.map((p) => p.ocupados);
  /*
    Domínio justo, não ancorado no zero.

    São ~5.600 endereços num total de ~12.700, e a variação de um mês cabe em
    algumas centenas. Começar no zero achataria a curva até virar uma reta — e
    a razão de existir deste cartão é justamente enxergar a variação do mês. A
    margem é 4% da faixa, para a linha não encostar nas bordas.
  */
  const faixa = Math.max(...ocupados) - Math.min(...ocupados);
  const folga = Math.max(10, Math.round(faixa * 0.04));
  const dominio: [number, number] = [
    Math.max(0, Math.min(...ocupados) - folga),
    Math.max(...ocupados) + folga,
  ];

  return (
    <Cartao className="flex flex-col">
      <CabecalhoCartao>
        <span className="min-w-0">
          <TituloCartao>Ocupação da empresa nos últimos 30 dias</TituloCartao>
          {/* O número do último fechamento em texto, ao lado do gráfico: é o
              que alguém copia para um e-mail sem ter de ler pixel de curva. */}
          <p className="tabular text-[11.5px] text-textoFraco">
            {numero(ultimo.ocupados)} de {numero(ultimo.total)} endereços em{' '}
            {dataCurta(ultimo.data)} · {numero(somados)}{' '}
            {somados === 1 ? 'estoque' : 'estoques'}
          </p>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {/* A pílula acompanha a curva: ela é de endereços, como o gráfico.
              O p.p. desce para a linha de apoio — continua útil para comparar
              com outro CD, mas não é mais o número que a curva conta. */}
          <Pilula tom={deltaEnderecos >= 0 ? 'alerta' : 'ok'}>
            {deltaEnderecos >= 0 ? '+' : ''}
            {numero(deltaEnderecos)} endereços
          </Pilula>
          <span className="tabular hidden text-[11px] text-textoFraco sm:inline">
            {delta >= 0 ? '+' : ''}
            {delta.toFixed(1)} p.p. · pico {numero(pico.ocupados)} em {dataCurta(pico.data)}
          </span>
        </span>
      </CabecalhoCartao>
      <div className="h-[228px] p-4 pl-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={serie as PontoSerieEmpresa[]}
            margin={{ top: 6, right: 10, bottom: 0, left: 0 }}
          >
            <defs>
              <linearGradient id="gradienteOcupacao" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgb(var(--cor-primaria))" stopOpacity={0.32} />
                <stop offset="100%" stopColor="rgb(var(--cor-primaria))" stopOpacity={0.01} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgb(var(--cor-borda))" vertical={false} strokeDasharray="3 4" />
            <XAxis
              dataKey="data"
              tickFormatter={(valor) => dataCurta(String(valor)).slice(0, 5)}
              tick={{ fontSize: 11, fill: 'rgb(var(--cor-texto-fraco))' }}
              tickLine={false}
              axisLine={false}
              minTickGap={28}
            />
            <YAxis
              domain={dominio}
              tickFormatter={(valor) => numero(Number(valor))}
              tick={{ fontSize: 11, fill: 'rgb(var(--cor-texto-fraco))' }}
              tickLine={false}
              axisLine={false}
              width={52}
            />
            <Tooltip
              cursor={{ stroke: 'rgb(var(--cor-primaria))', strokeDasharray: '3 3' }}
              contentStyle={{
                background: 'rgb(var(--cor-superficie))',
                border: '1px solid rgb(var(--cor-borda))',
                borderRadius: 12,
                fontSize: 12,
                boxShadow: 'var(--sombra-media)',
                color: 'rgb(var(--cor-texto))',
              }}
              labelFormatter={(rotulo) => dataCurta(String(rotulo))}
              /* O ponto inteiro no tooltip: contagem, total e percentual. A
                 curva mostra só a contagem; sem o total ao lado, uma mudança de
                 denominador passaria despercebida — que é exatamente o defeito
                 que tirou o percentual da série. */
              formatter={(valor, _nome, item) => {
                const p = item?.payload as PontoSerieEmpresa | undefined;
                return [
                  p
                    ? `${numero(Number(valor))} de ${numero(p.total)} · ${percentual(p.percentual)}`
                    : numero(Number(valor)),
                  'endereços ocupados',
                ];
              }}
            />
            <Area
              type="monotone"
              dataKey="ocupados"
              stroke="rgb(var(--cor-primaria))"
              strokeWidth={2.5}
              fill="url(#gradienteOcupacao)"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: 'rgb(var(--cor-superficie))' }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Cartao>
  );
}

function PrecisaDeAtencao({
  alertas,
}: {
  alertas: ReturnType<typeof derivarAlertas>;
}): JSX.Element {
  return (
    <Cartao className="flex flex-col">
      <CabecalhoCartao>
        <TituloCartao>Precisa de atenção</TituloCartao>
        {alertas.length > 0 ? <Pilula tom="erro">{alertas.length}</Pilula> : null}
      </CabecalhoCartao>
      {alertas.length === 0 ? (
        <EstadoVazio titulo="Nada pendente" detalhe="Nenhuma divergência ou alerta neste centro." />
      ) : (
        <ul className="divide-y divide-borda">
          {alertas.map((alerta) => (
            <li key={`${alerta.tipo}-${alerta.titulo}`}>
              <Link
                to={alerta.href}
                className="group flex items-start gap-3 px-4 py-3.5 transition-colors hover:bg-superficie2"
              >
                <span
                  className={cn(
                    'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border',
                    CLASSE_SEVERIDADE[alerta.severidade],
                  )}
                >
                  <CircleAlert className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-semibold">{alerta.titulo}</span>
                  <span className="block text-[12px] leading-relaxed text-textoFraco">
                    {alerta.detalhe}
                  </span>
                </span>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 -translate-x-1 text-textoSuave opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Cartao>
  );
}

/**
 * Ocupação dos centros da empresa, com os estoques que entram na conta.
 *
 * Existe porque a pergunta "quantas posições a empresa tem ocupadas" não tinha
 * resposta na tela: dava para ver 3.825 no 2201 e, para saber o resto, trocar
 * o centro no cabeçalho catorze vezes e somar na mão. A conta de cada linha é
 * a MESMA do KPI de ocupação do centro — o 2201 aqui é o 2201 lá.
 *
 * A caixa de cada centro recalcula os totais NA HORA, sem ida ao servidor: a
 * API manda as assinaturas de posição e a conta roda no cliente
 * (`lib/ocupacao.ts`). Foi o que permitiu marcar e desmarcar à vontade em vez
 * de esperar ~900ms por clique.
 *
 * Quem já vem marcado sai do cadastro (Administração > Centros): centro de
 * recebimento ou trânsito tem posição, mas é mercadoria de passagem e inflaria
 * a ocupação. Marcar um deles aqui continua possível — é uma pergunta, não uma
 * trava.
 */
/**
 * Ocupação da empresa — o TOTAL, e só ele.
 *
 * A lista dos estoques saiu daqui. Ela existia para deixar escolher quais
 * centros entram na conta, e com 16 linhas ocupava mais tela que o número que
 * veio explicar — numa página que agora também responde por vendas e
 * faturamento. O total é a pergunta desta seção; centro a centro é outra
 * pergunta, e ela já tem tela própria.
 *
 * O CONJUNTO SOMADO CONTINUA SENDO O DO CADASTRO — os centros que
 * contabilizam e têm fechamento, definidos em Administração > Centros de
 * estoque. O que se perdeu foi o ajuste manual aqui, não o critério; e o
 * número de estoques somados segue escrito ao lado do percentual, porque um
 * total sem dizer de quantos é um total sem dono.
 */
function OcupacaoDaEmpresa({
  consulta,
  selecao,
}: {
  consulta: UseQueryResult<OcupacaoEmpresaResposta, unknown>;
  /** Vem da página: o gráfico de 30 dias soma exatamente estes centros. */
  selecao: ReadonlySet<number>;
}): JSX.Element | null {
  const { empresaAtual } = useContexto();
  const dados = consulta.data;

  const total = React.useMemo(
    () => resumirOcupacao(dados?.assinaturas ?? [], selecao),
    [dados, selecao],
  );

  if (consulta.isLoading) return <Esqueleto className="h-64 rounded-xl" />;
  if (consulta.isError || !dados) return null;

  const empresa = dados.empresa;
  const comFechamento = dados.centros.filter((c) => c.dataBase !== null);
  const nomeEmpresa = empresaAtual?.nome
    ? `${empresa} · ${empresaAtual.nome}`
    : `empresa ${empresa}`;

  return (
    // `scroll-mt-20` porque o cabeçalho da aplicação é fixo: sem a margem, a
    // âncora para exatamente atrás dele e o título fica escondido.
    <Cartao id="ocupacao-empresa" className="scroll-mt-20">
      <CabecalhoCartao>
        <TituloCartao>Ocupação da empresa — {nomeEmpresa}</TituloCartao>
        <span className="flex items-center gap-2">
          <Pilula tom={total.percentualOcupacao >= 90 ? 'alerta' : 'neutro'}>
            {percentual(total.percentualOcupacao)}
          </Pilula>
          {/* Sem a lista, o contador deixa de ser controle e vira legenda —
              continua dizendo de quantos estoques é este total. */}
          <span className="tabular text-[11px] text-textoFraco">
            {numero(selecao.size)} de {numero(comFechamento.length)}{' '}
            {comFechamento.length === 1 ? 'estoque' : 'estoques'}
          </span>
        </span>
      </CabecalhoCartao>
      <CorpoCartao>
        {/*
          UM número de ocupação, não dois. A contagem é de ENDEREÇO físico:
          a mesma rua guarda venda, open box e reserva, e contá-la uma vez por
          centro dava um segundo total, igualmente "certo", que só servia para
          ninguém saber qual levar para a reunião. Endereço é o que responde
          "quanto do galpão está cheio" — e é a mesma pergunta da métrica
          "Localizadores" do Superset, então as duas telas passam a falar do
          mesmo objeto.
        */}
        <div className="mb-3 grid gap-2 sm:grid-cols-2">
          <ResumoEmpresa
            rotulo="Endereços ocupados"
            valor={numero(total.enderecosOcupados)}
            nota={`de ${numero(total.enderecos)} endereços físicos · ${percentual(
              total.percentualOcupacao,
            )}`}
          />
          <ResumoEmpresa
            rotulo="Endereços livres"
            valor={numero(total.enderecosLivres)}
            nota={
              total.enderecosNegativos > 0
                ? `${numero(total.enderecosNegativos)} endereços com saldo negativo`
                : 'prontos para alocar'
            }
          />
        </div>

        <p className="text-[11px] text-textoFraco">
          Quem entra na conta vem do cadastro em Administração &gt; Centros de estoque. Para ver
          estoque por estoque, troque o centro no cabeçalho.
        </p>
      </CorpoCartao>
    </Cartao>
  );
}

function ResumoEmpresa({
  rotulo,
  valor,
  nota,
}: {
  rotulo: string;
  valor: string;
  nota: string;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-borda bg-superficie2 px-3 py-2">
      <p className="text-[11px] uppercase tracking-wide text-textoFraco">{rotulo}</p>
      <p className="tabular text-[18px] font-bold leading-tight">{valor}</p>
      <p className="text-[11px] text-textoFraco">{nota}</p>
    </div>
  );
}

function EsqueletoInicio(): JSX.Element {
  return (
    <div className="space-y-4 p-3 sm:p-5">
      <div className="flex items-center gap-3">
        <Esqueleto className="h-11 w-11 rounded-xl" />
        <div className="space-y-2">
          <Esqueleto className="h-3 w-24" />
          <Esqueleto className="h-6 w-60" />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Esqueleto key={i} className="h-40 rounded-xl" />
        ))}
      </div>
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <Esqueleto className="h-72 rounded-xl" />
        <Esqueleto className="h-72 rounded-xl" />
      </div>
    </div>
  );
}

/* ==========================================================================
   PEDIDOS — o bloco de vendas da tela inicial
   ==========================================================================

   Três perguntas que a operação faz de manhã, na ordem em que ela as faz:

     1. quanto entrou e onde está parado  -> o funil da esteira;
     2. de qual marketplace vem            -> a tabela por canal;
     3. o que o ML prometeu para cada dia  -> o agendamento.

   O recorte de 3 dias vale para (1) e (2), que olham o que ENTROU. O (3) olha
   o que SAI, e por isso não tem janela: um pedido de cinco dias agendado para
   a semana que vem é justamente o que essa visão existe para mostrar.
*/

/** Rótulo e cor de cada canal. A cor é identidade, e não muda com o filtro. */
const CANAIS: Record<CanalMarketplace, { rotulo: string; cor: string }> = {
  meli: { rotulo: 'Mercado Livre', cor: 'rgb(var(--mkt-meli))' },
  shopee: { rotulo: 'Shopee', cor: 'rgb(var(--mkt-shopee))' },
  tiktok: { rotulo: 'TikTok', cor: 'rgb(var(--mkt-tiktok))' },
  outro: { rotulo: 'Outros', cor: 'rgb(var(--mkt-outro))' },
};

/** Os cinco degraus da rampa da esteira, na ordem do funil. */
const DEGRAUS = [
  'rgb(var(--fun-1))',
  'rgb(var(--fun-2))',
  'rgb(var(--fun-3))',
  'rgb(var(--fun-4))',
  'rgb(var(--fun-5))',
];

/**
 * A seção de vendas — DUAS METADES, e cada uma responde uma pergunta.
 *
 * À esquerda, o PEDIDO: o que entrou e onde travou. À direita, a NOTA: o que
 * saiu e em que ritmo. São as duas pontas do mesmo dia e por isso ficam lado a
 * lado, mas em colunas separadas: empilhadas, o olho lê o gráfico de barras do
 * agendamento logo abaixo do de faturamento e soma dois números que não se
 * somam.
 *
 * Cada coluna tem a sua consulta. Uma falhando mostra o erro só no lado dela —
 * o faturamento do dia continua na tela mesmo que o funil de 3 dias caia.
 */
function VendasDoDia({ empresa }: { empresa: number }): JSX.Element {
  const cliente = useQueryClient();
  const pedidos = usePainelPedidos(empresa);
  const dados = pedidos.data;

  return (
    <>
      <FaixaSecao
        icone={<ShoppingCart className="h-[18px] w-[18px]" />}
        titulo="Vendas"
        detalhe={
          dados
            ? `Pedidos dos últimos ${dados.dias} dias · faturamento de hoje` +
              (dados.empresas.length > 1 ? ` · empresas ${dados.empresas.join(' + ')}` : '')
            : 'Pedidos e faturamento'
        }
        acao={<BotaoAtualizar aoAtualizar={() => atualizarVendas(cliente, empresa)} />}
      />
      {/*
        AS DUAS COLUNAS TÊM A MESMA ALTURA, e o faturamento usa a dele inteira.

        Era `items-start`, e com isso o cartão da direita terminava na altura do
        conteúdo dele — sobrava um vão branco do tamanho da diferença para os
        três cartões da esquerda. Esticado, o gráfico fica no topo e a TABELA
        hora a hora cresce até o pé da seção, que é onde ela para de esconder
        linha: num dia adiantado são 12 horas, e antes só cabiam 4 sem rolar.
      */}
      <div className="grid gap-3 xl:grid-cols-2">
        <div className="grid gap-3">
          {pedidos.isError ? (
            <EstadoErro erro={pedidos.error} aoTentarDeNovo={() => void pedidos.refetch()} />
          ) : !dados ? (
            <>
              <Esqueleto className="h-[236px] rounded-xl" />
              <Esqueleto className="h-[190px] rounded-xl" />
              <Esqueleto className="h-[260px] rounded-xl" />
            </>
          ) : (
            <>
              <FunilDePedidos dados={dados} />
              <PorMarketplace dados={dados} />
              <AgendamentoMeli dados={dados} />
            </>
          )}
        </div>

        <FaturamentoDoDia empresa={empresa} />
      </div>
    </>
  );
}

/**
 * `82` -> `3d`; `9` -> `9h`; `null` -> `—`.
 *
 * Hora até um dia, dia depois disso: "82h" obriga quem lê a dividir por 24 no
 * meio do turno, e a decisão que esse número sustenta é "isso está velho?",
 * não "quantas horas exatamente?".
 */
function idade(horas: number | null): string {
  if (horas === null) return '—';
  if (horas < 24) return `${horas}h`;
  return `${Math.floor(horas / 24)}d`;
}

/** `2026-09-14` -> `14/09`. Sem `new Date`, que reinterpretaria o fuso. */
function formatarDiaBr(iso: string): string {
  const [, m, d] = iso.split('-');
  return d === undefined ? iso : `${d}/${m}`;
}

/** O dia da semana de um `aaaa-mm-dd`, sem deixar o fuso mover a data. */
function diaDaSemana(iso: string): string {
  const [a, m, d] = iso.split('-').map(Number);
  if (a === undefined || m === undefined || d === undefined) return '';
  const nomes = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'];
  return nomes[new Date(a, m - 1, d).getDay()] ?? '';
}

/** Hoje em São Paulo, `aaaa-mm-dd` — o mesmo critério do painel de expedição. */
function hojeEmSaoPaulo(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
}

/**
 * O funil da esteira: quanto entrou e em que degrau está parado.
 *
 * A composição vira UMA barra empilhada, e não cinco barras lado a lado. A
 * pergunta aqui é de proporção — "o quanto do que entrou ainda não andou" —, e
 * proporção se lê de uma vez numa barra inteira; cinco barras separadas
 * obrigam a somar de cabeça para chegar no mesmo lugar.
 *
 * Os degraus são vizinhos da mesma rampa, então o vão de 2px entre eles não é
 * estética: sem ele dois degraus encostados viram um bloco só.
 */
function FunilDePedidos({ dados }: { dados: PainelPedidosResposta }): JSX.Element {
  const { funil, total } = dados;

  return (
    <Cartao className="flex flex-col">
      <CabecalhoCartao>
        <TituloCartao>Na esteira</TituloCartao>
        <span className="text-[11px] text-textoFraco">estado atual no ERP</span>
      </CabecalhoCartao>
      <CorpoCartao className="flex flex-1 flex-col gap-2.5">
        <p className="tabular flex items-baseline gap-2 leading-none">
          <span className="text-[32px] font-bold tracking-[-0.03em] text-primaria">
            {numero(total)}
          </span>
          <span className="text-[13px] text-textoFraco">
            {total === 1 ? 'pedido' : 'pedidos'} em {dados.dias} dias
          </span>
        </p>

        {total === 0 ? (
          <EstadoVazio
            titulo="Nenhum pedido nestes estados"
            detalhe="Nada entrou na esteira na janela escolhida."
          />
        ) : (
          <>
            {/* A composição inteira numa barra. `gap` desenha o vão de 2px sem
                precisar de margem negativa em cada segmento. */}
            <div
              className="flex h-3 w-full gap-[2px] overflow-hidden rounded-full"
              role="img"
              aria-label={funil
                .map((f) => `${f.rotulo}: ${f.pedidos} (${f.percentual}%)`)
                .join('; ')}
            >
              {funil.map((f, i) =>
                f.pedidos === 0 ? null : (
                  <span
                    key={f.estado}
                    className="h-full first:rounded-l-full last:rounded-r-full"
                    style={{ width: `${f.percentual}%`, background: DEGRAUS[i] }}
                  />
                ),
              )}
            </div>

            {/*
              A LISTA REPETE A BARRA EM NÚMERO, e isso é a acessibilidade dela.

              Os cinco degraus são do mesmo matiz — de propósito, porque a
              esteira tem ordem —, e matiz único quer dizer que quem não
              distingue claridade fina não separa os dois do meio. O nome
              escrito ao lado do quadradinho resolve: a cor deixa de ser o
              único portador da identidade.
            */}
            <ul className="grid gap-1">
              {funil.map((f, i) => (
                <li key={f.estado} className="flex items-center gap-2 text-[12.5px]">
                  <span
                    aria-hidden
                    className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                    style={{ background: DEGRAUS[i] }}
                  />
                  <span
                    className={cn('min-w-0 flex-1 truncate', f.pedidos === 0 && 'text-textoSuave')}
                  >
                    {f.rotulo}
                  </span>
                  {/*
                    A IDADE DO MAIS ANTIGO, ao lado da contagem.

                    572 parados é um número; 572 parados com o primeiro deles há
                    três dias é um problema. Era a diferença entre uma fila que
                    se formou hoje de manhã e uma que ninguém tocou desde
                    segunda — e a tela mostrava as duas igual.
                  */}
                  <span
                    className={cn(
                      'tabular w-14 shrink-0 text-right text-[11.5px]',
                      f.horasDoMaisAntigo !== null && f.horasDoMaisAntigo >= 48
                        ? 'font-semibold text-status-atencao'
                        : 'text-textoFraco',
                    )}
                    title={
                      f.horasDoMaisAntigo === null
                        ? undefined
                        : `O mais antigo deste estado entrou há ${f.horasDoMaisAntigo} horas`
                    }
                  >
                    {idade(f.horasDoMaisAntigo)}
                  </span>
                  <span
                    className={cn(
                      'tabular w-12 shrink-0 text-right font-semibold',
                      f.pedidos === 0 ? 'text-textoSuave' : 'text-texto',
                    )}
                  >
                    {numero(f.pedidos)}
                  </span>
                  <span className="tabular w-12 shrink-0 text-right text-textoFraco">
                    {percentual(f.percentual)}
                  </span>
                </li>
              ))}
            </ul>

            {/*
              O ENCALHADO, que a janela de 3 dias escondia.

              Medido em 17/09 na empresa 32: a tela dizia 749 e havia 1.546
              pedidos mais velhos que a janela nos mesmos estados — 652 deles
              com a carga já montada. Não entram na conta do funil de propósito
              (a pergunta ali é "o que entrou agora"), mas ficar invisível fazia
              o painel dizer que a fila era metade do que é.
            */}
            {dados.foraDaJanela > 0 ? (
              <p className="rounded-lg border border-status-atencao/30 bg-status-atencaoFundo px-2.5 py-2 text-[11.5px] leading-snug">
                <b className="tabular text-texto">+{numero(dados.foraDaJanela)}</b>{' '}
                <span className="text-textoFraco">
                  nos mesmos estados, porém mais antigos que {dados.dias} dias — fora desta conta.
                  O horizonte é de {dados.horizonteDias} dias, o mesmo que o ERP usa para liberar
                  separação.
                </span>
              </p>
            ) : null}
          </>
        )}
      </CorpoCartao>
    </Cartao>
  );
}

/**
 * Por marketplace — TABELA, e de propósito.
 *
 * O pedido era "total por status de marketplace, sem poluir a tela". Isso são
 * quatro canais por cinco estados: num gráfico viram vinte marcas coloridas
 * disputando espaço, e ninguém consegue ler o número exato de nenhuma. A
 * matriz responde a mesma pergunta com os números na mão e sem tinta nenhuma
 * além do ponto de identidade do canal.
 *
 * O ponto colorido existe para casar esta tabela com qualquer outra visão por
 * canal; o NOME ao lado é o que carrega a informação, porque o par ciano/rosa
 * da paleta fica na faixa de piso do teste para tritanopia.
 */
function PorMarketplace({ dados }: { dados: PainelPedidosResposta }): JSX.Element {
  const { canais, funil, total } = dados;

  return (
    <Cartao className="flex flex-col">
      <CabecalhoCartao>
        <TituloCartao>Por marketplace</TituloCartao>
        <span className="text-[11px] text-textoFraco">mesmos {dados.dias} dias</span>
      </CabecalhoCartao>
      <CorpoCartao className="flex-1 p-0">
        {canais.length === 0 ? (
          <EstadoVazio titulo="Nenhum pedido na janela" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="text-left text-[10px] uppercase tracking-wide text-textoFraco">
                <tr className="border-b border-borda">
                  <th className="px-3 py-1.5 font-semibold">Canal</th>
                  <th className="px-1.5 py-1.5 text-right font-semibold">Total</th>
                  {/* Cabeçalho curto: a coluna é estreita e o nome inteiro
                      ("Pagamento confirmado") quebraria em três linhas. O
                      title devolve o nome completo a quem passar o mouse. */}
                  {funil.map((f, i) => (
                    <th
                      key={f.estado}
                      title={f.rotulo}
                      className={cn(
                        'px-1.5 py-1.5 text-right font-semibold',
                        i === funil.length - 1 && 'pr-3',
                      )}
                    >
                      {abreviar(f.rotulo)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-borda">
                {canais.map((c) => (
                  <tr key={c.canal} className="transition-colors hover:bg-superficie2">
                    <td className="px-3 py-1.5">
                      <span className="flex items-center gap-2">
                        <span
                          aria-hidden
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ background: CANAIS[c.canal].cor }}
                        />
                        <span className="truncate font-medium">{CANAIS[c.canal].rotulo}</span>
                      </span>
                    </td>
                    <td className="tabular px-1.5 py-1.5 text-right font-bold">{numero(c.total)}</td>
                    {c.porEstado.map((n, i) => (
                      <td
                        key={funil[i]?.estado ?? i}
                        className={cn(
                          'tabular px-1.5 py-1.5 text-right',
                          i === c.porEstado.length - 1 && 'pr-3',
                          n === 0 ? 'text-textoSuave' : 'text-texto',
                        )}
                      >
                        {n === 0 ? '—' : numero(n)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-bordaForte text-[12px]">
                  <td className="px-3 py-1.5 font-semibold text-textoFraco">Total</td>
                  <td className="tabular px-1.5 py-1.5 text-right font-bold">{numero(total)}</td>
                  {funil.map((f, i) => (
                    <td
                      key={f.estado}
                      className={cn(
                        'tabular px-1.5 py-1.5 text-right text-textoFraco',
                        i === funil.length - 1 && 'pr-3',
                      )}
                    >
                      {f.pedidos === 0 ? '—' : numero(f.pedidos)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CorpoCartao>
    </Cartao>
  );
}

/** "Pagamento confirmado" -> "Pgto"; cabeçalho de coluna estreita. */
function abreviar(rotulo: string): string {
  const mapa: Record<string, string> = {
    'Pagamento confirmado': 'Pgto',
    'Em separação': 'Separ.',
    Conferido: 'Confer.',
    Faturado: 'Fatur.',
    'Em romaneio': 'Roman.',
  };
  return mapa[rotulo] ?? rotulo;
}

/**
 * O que o Mercado Livre agendou para cada dia.
 *
 * Só `PAGAMENTO CONFIRMADO` com `STATUS_ENVIO = buffered`: é o pedido que o ML
 * já encaixou numa coleta futura e que a operação ainda não começou. O eixo é
 * a data de SLA — o dia em que o veículo vem —, e por isso este cartão NÃO
 * respeita a janela de 3 dias dos outros dois: aqui a pergunta olha para a
 * frente, e cortar por data de entrada esconderia metade do compromisso.
 *
 * Barra e não linha: os dias são compromissos discretos, não uma série
 * contínua — e há buracos (o ML não coleta no domingo). Uma linha ligaria
 * sexta a segunda e desenharia um vale que não existe.
 */function AgendamentoMeli({ dados }: { dados: PainelPedidosResposta }): JSX.Element {
  const hoje = hojeEmSaoPaulo();
  /** O dia que o usuário clicou, enquanto ele decide o que fazer com ele. */
  const [escolhido, definirEscolhido] = React.useState<BarraDoDia | null>(null);

  /**
   * Os três próximos dias abertos, o resto somado.
   *
   * O eixo já foi um dia por coluna, e com isso a resposta da pergunta que se
   * faz de manhã — "o que eu tenho de despachar AMANHÃ" — ficava do mesmo
   * tamanho de um dia da semana que vem. O horizonte de decisão da operação
   * são três dias: o que está depois disso não muda o turno de hoje, mas
   * precisa aparecer para ninguém achar que a fila acabou.
   *
   * D+1 a D+3 entram SEMPRE, mesmo zerados. Um sábado sem coleta é informação:
   * some do gráfico e o volume de segunda parece ter vindo do nada.
   */
  const barras = React.useMemo(() => {
    const proximos = [1, 2, 3].map((n) => somarDias(hoje, n));

    let atrasado = 0;
    /* O vencido também monta onda, então soma o separável e a quebra por
       empresa de todos os dias que caem nele. */
    let atrasadoSeparavel = 0;
    const atrasadoPorEmpresa = new Map<number, number>();
    let depois = 0;
    let depoisVolume = 0;
    const porDia = new Map<string, DiaAgendado>();

    for (const d of dados.agendado) {
      /* Sem data no mesmo balde do vencido: não saber quando vence não é ter
         prazo, e é o que a operação precisa olhar primeiro. */
      if (d.dia === null || d.dia <= hoje) {
        atrasado += d.falta;
        /* Sem data não entra no separável: a onda filtra por prazo até hoje, e
           pedido sem prazo legível não passa nesse filtro. */
        if (d.dia !== null) {
          atrasadoSeparavel += d.separavel;
          for (const e of d.porEmpresa) {
            atrasadoPorEmpresa.set(e.empresa, (atrasadoPorEmpresa.get(e.empresa) ?? 0) + e.pedidos);
          }
        }
      } else if (proximos.includes(d.dia)) {
        porDia.set(d.dia, d);
      } else {
        depois += d.falta;
        depoisVolume += d.pedidos;
      }
    }

    const saida: BarraDoDia[] = [];

    /* Só aparece quando existe — numa semana em dia, uma coluna "vencido"
       zerada seria um alarme desligado ocupando espaço. */
    if (atrasado > 0) {
      saida.push({
        chave: 'atrasado',
        rotulo: 'hoje / vencido',
        /*
          SEM "no dia" AQUI — este balde não é um dia.

          Ele soma tudo com prazo até hoje, e eu tinha posto o total desse
          intervalo no rótulo: virou "10.045 no dia" na Lisa, que é um mês de
          pedidos apresentado como se fosse a quarta-feira. Número irreal
          assusta e, pior, ensina a desconfiar do painel inteiro. O que este
          balde tem para dizer é quantos ainda faltam, e isso a barra já diz.
        */
        detalhe: 'prazo de coleta vencido ou vencendo hoje, ainda por fazer',
        /*
          ESTE BALDE É O ÚNICO QUE CONTA "O QUE FALTA", e de propósito.

          Nos dias futuros a pergunta é de volume: quanto sai amanhã. Num dia
          que já passou, volume não quer dizer nada — a Lisa teve 591 ontem e
          todos foram embora. O que sobra de um dia vencido é só o que ficou
          para trás, e é isso que precisa gritar.
        */
        pedidos: atrasado,
        aFazer: atrasado,
        separavel: atrasadoSeparavel,
        tipo: 'atrasado',
        dia: null,
        porEmpresa: [...atrasadoPorEmpresa]
          .map(([empresa, pedidos]) => ({ empresa, pedidos }))
          .sort((a, b) => b.pedidos - a.pedidos),
      });
    }

    for (const dia of proximos) {
      const d = porDia.get(dia);
      saida.push({
        chave: dia,
        rotulo: formatarDiaBr(dia),
        detalhe: `${diaDaSemana(dia)} · ${numero(d?.falta ?? 0)} por fazer · ${numero(
          d?.separavel ?? 0,
        )} para separar`,
        pedidos: d?.pedidos ?? 0,
        aFazer: d?.falta ?? 0,
        separavel: d?.separavel ?? 0,
        tipo: 'dia',
        dia,
        porEmpresa: d?.porEmpresa ?? [],
      });
    }

    if (depoisVolume > 0) {
      saida.push({
        chave: 'depois',
        rotulo: 'próximos dias',
        /* Mesmo motivo do balde de vencidos: são vários dias somados, e
           chamar isso de "no dia" seria inventar um dia que não existe. */
        detalhe: `tudo agendado a partir de ${formatarDiaBr(somarDias(hoje, 4))}`,
        pedidos: depoisVolume,
        aFazer: depois,
        separavel: 0,
        tipo: 'depois',
        dia: null,
        porEmpresa: [],
      });
    }

    return saida;
  }, [dados.agendado, hoje]);

  const atrasados = barras.find((b) => b.tipo === 'atrasado')?.pedidos ?? 0;
  const depois = barras.find((b) => b.tipo === 'depois')?.pedidos ?? 0;
  const noHorizonte = barras
    .filter((b) => b.tipo === 'dia')
    .reduce((a, b) => a + b.pedidos, 0);
  const aFazerNoHorizonte = barras
    .filter((b) => b.tipo === 'dia')
    .reduce((a, b) => a + b.aFazer, 0);
  /*
    O total do cartão sai das PRÓPRIAS BARRAS.

    Vindo do servidor ele somava o horizonte inteiro de 30 dias para trás —
    15.711 pedidos, quase todos despachados havia semanas. Somando o que está
    desenhado, o título nunca pode divergir do gráfico embaixo dele.
  */
  const totalNoCartao = barras.reduce((a, b) => a + b.pedidos, 0);

  return (
    <Cartao className="flex flex-col">
      <CabecalhoCartao>
        <TituloCartao>Agendado no Mercado Livre · {numero(totalNoCartao)}</TituloCartao>
        <span className="flex flex-wrap items-center gap-2 text-[11px] text-textoFraco">
          <Pilula
            tom="neutro"
            title="Pagamento confirmado, em separação ou conferido — o que ainda depende de nós nos 3 dias"
          >
            {numero(aFazerNoHorizonte)} a fazer
          </Pilula>
          {atrasados > 0 ? <Pilula tom="erro">{numero(atrasados)} vencido ou hoje</Pilula> : null}
        </span>
      </CabecalhoCartao>
      <CorpoCartao>
        {dados.agendadoTotal === 0 ? (
          <EstadoVazio
            icone={<CalendarClock className="h-7 w-7" />}
            titulo="Nada agendado"
            detalhe="Nenhum pedido aguardando coleta do Mercado Livre."
          />
        ) : (
          <>
            <p className="mb-2 text-[12px] text-textoFraco">
              Os <b className="text-texto">3 próximos dias</b> de coleta, um a um —{' '}
              <b className="tabular text-texto">{numero(noHorizonte)}</b>{' '}
              {noHorizonte === 1 ? 'pedido' : 'pedidos'} no horizonte, dos quais{' '}
              <b className="tabular text-texto">{numero(aFazerNoHorizonte)}</b> ainda dependem de
              nós.
              {depois > 0 ? ' A última coluna soma tudo que vem depois disso.' : ''}
            </p>
            <div className="h-[190px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barras} margin={{ top: 18, right: 4, bottom: 0, left: -18 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="rgb(var(--cor-borda))"
                  />
                  <XAxis
                    dataKey="rotulo"
                    tick={{ fontSize: 11.5, fill: 'rgb(var(--cor-texto-fraco))' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgb(var(--cor-borda))' }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'rgb(var(--cor-texto-fraco))' }}
                    tickLine={false}
                    axisLine={false}
                    width={44}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgb(var(--cor-superficie-3))' }}
                    contentStyle={{
                      borderRadius: 12,
                      border: '1px solid rgb(var(--cor-borda))',
                      background: 'rgb(var(--cor-superficie))',
                      fontSize: 12,
                    }}
                    labelFormatter={(r: string) => {
                      const b = barras.find((x) => x.rotulo === r);
                      return b?.detalhe ? `${r} · ${b.detalhe}` : r;
                    }}
                    formatter={(v: number) => [numero(v), 'agendados']}
                  />
                  {/* Rótulo em cima de cada barra: são poucas colunas e o número
                      exato é a razão de olhar o cartão — obrigar a passar o
                      mouse para lê-lo seria esconder a resposta. */}
                  {/*
                    O clique é só nos DIAS. `onClick` do Bar dispara em
                    qualquer coluna, então o filtro do tipo mora aqui — e o
                    cursor só vira mãozinha onde há o que clicar, senão a tela
                    promete uma ação que não acontece.
                  */}
                  <Bar
                    dataKey="pedidos"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={56}
                    onClick={(p: unknown) => {
                      const b = (p as { payload?: BarraDoDia })?.payload;
                      /*
                        O clique só abre com pedido SEPARÁVEL, não com "a fazer".

                        Medido em 18/09: 340 pedidos ainda dependiam de nós e só
                        2 podiam entrar em onda — os outros 338 já estavam numa.
                        Abrir a tela com os 340 na promessa fazia ela nascer
                        vazia, logo depois de o painel garantir que havia
                        centenas naquele dia.
                      */
                      if (b && (b.tipo === 'dia' || b.tipo === 'atrasado') && b.separavel > 0) {
                        definirEscolhido(b);
                      }
                    }}
                  >
                    <LabelList
                      dataKey="pedidos"
                      position="top"
                      offset={6}
                      className="fill-[rgb(var(--cor-texto-fraco))] text-[11px] font-semibold"
                      formatter={(v: number) => (v === 0 ? '' : numero(v))}
                    />
                    {barras.map((b) => (
                      <Cell
                        key={b.chave}
                        fill={CORES_AGENDA[b.tipo]}
                        cursor={
                          (b.tipo === 'dia' || b.tipo === 'atrasado') && b.separavel > 0
                            ? 'pointer'
                            : 'default'
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* A ação precisa estar escrita: barra clicável não se anuncia
                sozinha, e quem não descobre o clique não usa o atalho. */}
            <p className="mt-1 text-[11.5px] text-textoFraco">
              Clique num dos <b className="text-texto">três dias</b> ou no{' '}
              <b className="text-texto">vencido ou hoje</b> para montar a onda de separação já
              filtrada por aquela coleta.
            </p>

            {/*
              A legenda só nomeia o que está desenhado.

              O cinza da última coluna não é "mais um dia": é um acumulado de
              vários, e sem essa linha alguém compara a altura dela com a de
              amanhã como se fossem a mesma unidade. Cor de status (o vermelho
              do vencido) nunca viaja sem o nome escrito ao lado.
            */}
            <p className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] text-textoFraco">
              {atrasados > 0 ? (
                <span className="flex items-center gap-1.5">
                  <span
                    aria-hidden
                    className="h-2.5 w-2.5 rounded-[3px]"
                    style={{ background: CORES_AGENDA.atrasado }}
                  />
                  vencido ou hoje
                </span>
              ) : null}
              <span className="flex items-center gap-1.5">
                <span
                  aria-hidden
                  className="h-2.5 w-2.5 rounded-[3px]"
                  style={{ background: CORES_AGENDA.dia }}
                />
                um dia de coleta
              </span>
              {depois > 0 ? (
                <span className="flex items-center gap-1.5">
                  <span
                    aria-hidden
                    className="h-2.5 w-2.5 rounded-[3px]"
                    style={{ background: CORES_AGENDA.depois }}
                  />
                  soma dos dias seguintes — não é um dia
                </span>
              ) : null}
            </p>
          </>
        )}
      </CorpoCartao>

      {escolhido !== null && (escolhido.dia !== null || escolhido.tipo === 'atrasado') ? (
        <MontarOndaDoDia
          dia={escolhido.dia ?? hoje}
          ate={escolhido.tipo === 'atrasado'}
          rotulo={escolhido.rotulo}
          semana={escolhido.detalhe}
          pedidos={escolhido.pedidos}
          porEmpresa={escolhido.porEmpresa}
          aoFechar={() => definirEscolhido(null)}
        />
      ) : null}
    </Cartao>
  );
}

/**
 * As três naturezas de coluna do agendamento.
 *
 * `depois` é NEUTRO de propósito: pintá-lo com a cor de um dia faria alguém
 * comparar a altura dele com a de amanhã, e são unidades diferentes — um dia
 * contra a soma de muitos. O cinza diz "isto não é um dia" antes de qualquer
 * legenda ser lida.
 */
/** Uma coluna do agendamento. Só a de `tipo: 'dia'` é clicável. */
interface BarraDoDia {
  chave: string;
  rotulo: string;
  detalhe: string;
  /**
   * A altura da barra: quantos pedidos o ML espera coletar nesse dia.
   *
   * É a pergunta que o cartão se propõe a responder — "quanto sai amanhã" — e
   * o número que bate com a consulta que qualquer um faz na mão
   * (`DATA_SLA_MELI = '18/09/2026'`). Já tentei que fosse "o que falta fazer":
   * a barra do dia 18 marcava 572 onde a base dizia 590, e quem confere na mão
   * encontra a diferença e para de confiar no painel.
   *
   * Cancelados ficam de fora — não saem, e não são volume de dia nenhum.
   */
  pedidos: number;
  /**
   * Desses, quantos ainda dependem de nós: 22, 3 ou 28.
   *
   * Fica ao lado, não no lugar. Em 17/09 a Lisa tinha 591 no dia e ZERO por
   * fazer (535 em romaneio, 59 faturados) — o dia estava resolvido, e só este
   * segundo número conta isso.
   */
  aFazer: number;
  /**
   * Quantos desses o clique consegue levar para a onda.
   *
   * Nem tudo que falta pode virar onda: pedido em separação já tem a sua. Em
   * 18/09 a empresa 32 tinha 621 no dia, 340 dependendo de nós e **2**
   * separáveis — e a tela de onda abria vazia porque prometia os 340.
   */
  separavel: number;
  tipo: 'atrasado' | 'dia' | 'depois';
  /** `aaaa-mm-dd` — só nas barras de dia. */
  dia: string | null;
  porEmpresa: { empresa: number; pedidos: number }[];
}

const CORES_AGENDA: Record<'atrasado' | 'dia' | 'depois', string> = {
  atrasado: 'rgb(var(--st-divergencia))',
  dia: 'rgb(var(--cor-primaria))',
  depois: 'rgb(var(--st-indisponivel))',
};

/** `2026-09-17` + 2 -> `2026-09-19`. Em UTC, para o fuso não mover o dia. */
function somarDias(iso: string, dias: number): string {
  const [a, m, d] = iso.split('-').map(Number);
  if (a === undefined || m === undefined || d === undefined) return iso;
  const t = new Date(Date.UTC(a, m - 1, d + dias));
  return t.toISOString().slice(0, 10);
}

/**
 * Faturamento de hoje, hora a hora — a metade direita da seção de vendas.
 *
 * O EIXO É DINHEIRO, e a contagem de notas anda junto no texto.
 *
 * Tentar mostrar os dois no gráfico exigiria duas escalas no mesmo desenho, e
 * duas escalas é a forma mais rápida de fazer alguém ler uma correlação que
 * não existe. Uma nota de R$ 40 mil e vinte notas de R$ 200 são a mesma altura
 * em reais e alturas opostas em quantidade: quem precisa do ritmo da operação
 * lê a quantidade no rodapé e na dica; quem precisa do resultado lê a barra.
 */
function FaturamentoDoDia({ empresa }: { empresa: number }): JSX.Element {
  const consulta = useFaturamentoPorHora(empresa);
  const dados = consulta.data;

  if (consulta.isError) {
    return <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />;
  }
  if (!dados) return <Esqueleto className="h-[420px] rounded-xl" />;

  return <CartaoFaturamento dados={dados} />;
}

function CartaoFaturamento({ dados }: { dados: FaturamentoPorHoraResposta }): JSX.Element {
  /**
   * Da primeira hora com nota até a hora corrente.
   *
   * As horas VAZIAS DO MEIO entram: um buraco das 9h às 11h é o que faz
   * alguém perguntar o que aconteceu, e é a informação mais útil do cartão
   * num dia ruim. O que não entra é o futuro — desenhar as 24 horas faria o
   * dia parecer terminado às 9 da manhã, com dois terços do gráfico no chão.
   */
  const { barras, horaAgora } = React.useMemo(() => {
    const agora = Number(
      new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        hour: '2-digit',
        hour12: false,
      }).format(new Date()),
    );
    const porHora = new Map(dados.horas.map((h) => [h.hora, h]));
    const primeira = dados.horas.length === 0 ? agora : Math.min(...dados.horas.map((h) => h.hora));
    /* `max` com a hora corrente: nota lançada às 23h de um dia fiscal anterior
       existe, e o eixo não pode terminar antes do último dado. */
    const ultima = Math.max(agora, ...dados.horas.map((h) => h.hora));

    const saida = [];
    for (let h = primeira; h <= ultima; h += 1) {
      const d = porHora.get(h);
      saida.push({
        hora: h,
        rotulo: `${String(h).padStart(2, '0')}h`,
        valor: d?.valor ?? 0,
        notas: d?.notas ?? 0,
        emCurso: h === agora,
      });
    }
    return { barras: saida, horaAgora: agora };
  }, [dados.horas]);

  /* Média sobre as horas que PRODUZIRAM, não sobre o relógio: dividir pelas
     horas do dia inclui a madrugada e devolve um número que não descreve
     turno nenhum. */
  const horasAtivas = dados.horas.length;
  /*
    A média é de NOTAS por hora, não de reais.

    O cartão existe para medir produção, e produção não se conta em dinheiro:
    uma geladeira e um pen drive dão o mesmo trabalho para quem confere e
    emite. Medido em 17/09: às 14h saíram 4 notas somando R$ 365 mil, contra
    72 notas e R$ 184 mil às 6h — em reais, a hora fraca parece a melhor do
    dia.
  */
  const mediaPorHora = horasAtivas === 0 ? 0 : dados.notas / horasAtivas;
  /* A escala das barrinhas da tabela. Zero nunca divide. */
  const maiorHora = Math.max(1, ...dados.horas.map((h) => h.notas));
  const pico = dados.horas.find((h) => h.hora === dados.horaPico) ?? null;

  return (
    <Cartao className="flex flex-col">
      <CabecalhoCartao>
        <TituloCartao>Faturado hoje</TituloCartao>
        <span className="text-[11px] text-textoFraco">
          até as {String(horaAgora).padStart(2, '0')}h
        </span>
      </CabecalhoCartao>
      <CorpoCartao className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
          <p className="tabular leading-none">
            <span className="block text-[34px] font-bold tracking-[-0.03em] text-primaria">
              {moeda(dados.valor)}
            </span>
            <span className="mt-1.5 block text-[13px] text-textoFraco">
              {numero(dados.notas)} {dados.notas === 1 ? 'nota emitida' : 'notas emitidas'}
            </span>
          </p>
          <span className="flex gap-4 text-right">
            <span>
              <span className="rotulo block">Notas / hora</span>
              <span className="tabular mt-1 block text-[15px] font-bold">
                {numero(Math.round(mediaPorHora))}
              </span>
            </span>
            <span>
              <span className="rotulo block">Pico</span>
              <span className="tabular mt-1 block text-[15px] font-bold">
                {pico === null ? '—' : `${String(pico.hora).padStart(2, '0')}h`}
              </span>
            </span>
          </span>
        </div>

        {/*
          SOZINHO, "R$ 421 mil" NÃO DIZ SE O DIA ESTÁ BOM.

          A referência é o MESMO DIA DA SEMANA nas últimas quatro semanas, até
          a mesma hora — comparar com ontem seria comparar segunda com domingo
          (medido: 17 notas no domingo contra 1.196 na segunda, e o painel
          acusaria +6.900%). É a mediana, porque quatro amostras com uma ponta
          de R$ 222 mil fazem a média descrever um dia que não existiu.
        */}
        {dados.referencia !== null && dados.referencia.valorTipico > 0 ? (
          <ComparacaoComReferencia valor={dados.valor} referencia={dados.referencia} />
        ) : null}

        {dados.notas === 0 ? (
          <EstadoVazio
            icone={<Receipt className="h-7 w-7" />}
            titulo="Nenhuma nota hoje"
            detalhe="Nada foi faturado neste dia fiscal até agora."
          />
        ) : (
          <>
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barras} margin={{ top: 12, right: 4, bottom: 0, left: -6 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="rgb(var(--cor-borda))"
                  />
                  <XAxis
                    dataKey="rotulo"
                    tick={{ fontSize: 11, fill: 'rgb(var(--cor-texto-fraco))' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgb(var(--cor-borda))' }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 10.5, fill: 'rgb(var(--cor-texto-fraco))' }}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                    tickFormatter={(v: number) => numero(v)}
                    allowDecimals={false}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgb(var(--cor-superficie-3))' }}
                    contentStyle={{
                      borderRadius: 12,
                      border: '1px solid rgb(var(--cor-borda))',
                      background: 'rgb(var(--cor-superficie))',
                      fontSize: 12,
                    }}
                    labelFormatter={(r: string) => {
                      const b = barras.find((x) => x.rotulo === r);
                      return b?.emCurso ? `${r} · hora em curso` : r;
                    }}
                    /* O valor entra AQUI, e não num segundo eixo: a barra
                       responde pela produção, e quem passa o mouse quer as duas
                       leituras do mesmo instante. Dois eixos y seriam duas
                       escalas disputando a mesma altura. */
                    formatter={(v: number, _n: string, item: { payload?: { valor?: number } }) => [
                      `${numero(v)} ${v === 1 ? 'nota' : 'notas'} · ${moeda(item.payload?.valor ?? 0)}`,
                      'emitido',
                    ]}
                  />
                  {/*
                    A ALTURA É A CONTAGEM DE NOTAS.

                    Era o valor faturado, e com isso o gráfico media o tamanho
                    do que saiu, não quanto gente produziu: em 17/09 a coluna
                    das 14h (4 notas, R$ 365 mil) era a maior do dia, três vezes
                    a das 6h (72 notas, R$ 184 mil). Quem olhasse para cobrar
                    ritmo cobraria exatamente ao contrário.
                  */}
                  <Bar dataKey="notas" radius={[4, 4, 0, 0]} maxBarSize={46}>
                    {barras.map((b) => (
                      <Cell
                        key={b.hora}
                        fill={
                          b.emCurso ? 'rgb(var(--cor-primaria-2))' : 'rgb(var(--cor-primaria))'
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/*
              A hora corrente é uma barra INCOMPLETA, e sem dizer isso ela vira
              uma queda: às 8h05 o gráfico mostra a última coluna no chão e
              quem lê acha que a operação parou, quando ela mal começou a hora.
            */}
            {/* Nomeia a HORA, não a posição: nota lançada de madrugada num dia
                fiscal anterior empurra o eixo para além de agora, e aí "a
                última coluna" aponta para a barra errada. */}
            <p className="text-[11.5px] text-textoFraco">
              A coluna de{' '}
              <b className="tabular text-texto">{String(horaAgora).padStart(2, '0')}h</b> é a hora
              em curso — ainda não fechou.
            </p>

            {/*
              O NÚMERO EXATO, HORA A HORA.

              A barra responde "como foi o ritmo"; ela não responde "quanto
              exatamente saiu às 7h", que é a pergunta de quem vai comparar com
              outro relatório ou cobrar um turno. Ler isso de um eixo é chute.
              A tabela também é a versão da informação que não depende de
              enxergar cor nem de passar o mouse.
            */}
            {/* `flex-1 min-h-0`: cresce com o cartão e ainda rola sozinha
                quando as horas passam do que cabe. Sem o `min-h-0` o flex se
                recusa a encolher e a rolagem vaza para a página. */}
            <div className="-mx-4 mt-1 min-h-0 flex-1 overflow-y-auto border-t border-borda">
              <table className="w-full text-[12px]">
                <thead className="sticky top-0 z-10 bg-superficie text-left text-[10px] uppercase tracking-wide text-textoFraco">
                  <tr className="border-b border-borda">
                    <th className="px-4 py-1.5 font-semibold">Hora</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Notas</th>
                    {/* Sem rótulo: a barra é a mesma coluna de Notas, lida de
                        relance. Um título aqui prometeria um terceiro dado. */}
                    <th className="w-[22%] px-1 py-1.5" aria-hidden />
                    <th className="px-4 py-1.5 text-right font-semibold">Faturado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-borda">
                  {barras.map((b) => (
                    <tr
                      key={b.hora}
                      className={cn(
                        'transition-colors hover:bg-superficie2',
                        b.notas === 0 && 'text-textoSuave',
                      )}
                    >
                      <td className="tabular px-4 py-1.5 font-medium">
                        {b.rotulo}
                        {b.emCurso ? (
                          <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-primaria">
                            em curso
                          </span>
                        ) : null}
                      </td>
                      <td className="tabular px-2 py-1.5 text-right">
                        {b.notas === 0 ? '—' : numero(b.notas)}
                      </td>
                      {/*
                        A MESMA CONTAGEM, EM COMPRIMENTO.

                        O número diz quanto; a barra diz como foi o ritmo sem
                        obrigar a comparar dezenas de números na vertical. É a
                        leitura que a tabela não dava e o gráfico dá — só que
                        aqui ela fica ao lado do valor exato, na mesma linha.
                      */}
                      <td className="px-1 py-1.5">
                        <span className="block h-[4px] w-full rounded-full bg-superficie3">
                          <span
                            className={cn(
                              'block h-full rounded-full',
                              b.emCurso ? 'bg-primaria-2' : 'bg-primaria/60',
                            )}
                            style={{ width: `${(b.notas / maiorHora) * 100}%` }}
                          />
                        </span>
                      </td>
                      <td className="tabular px-4 py-1.5 text-right font-semibold">
                        {b.notas === 0 ? '—' : moeda(b.valor)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CorpoCartao>
    </Cartao>
  );
}

/**
 * Clicar no dia abre a decisão de montar a onda daquela coleta.
 *
 * O CLIQUE NÃO EMITE NADA. Ele leva para a Separação com a fila já recortada,
 * e é lá que a onda se monta e se emite — com os filtros extras que a operação
 * quiser (SKU, seção, UF) e com a conferência do roteiro. Emitir direto daqui
 * seria gravar no ERP a partir de um gráfico, sem ninguém ver a lista.
 *
 * OS TRÊS DIAS ABERTOS E O VENCIDO SÃO CLICÁVEIS. "Próximos dias" é a soma de
 * muitos, e uma onda com o SLA de duas semanas dentro não é uma carga.
 * "Hoje / vencido" também mistura datas, mas é o que tem de sair primeiro — a
 * onda dele é por prazo ATÉ hoje (`ate`), não por um dia exato.
 */
function MontarOndaDoDia({
  dia,
  ate = false,
  rotulo,
  semana,
  pedidos,
  porEmpresa,
  aoFechar,
}: {
  dia: string;
  /** `dia` é o LIMITE (vencido ou hoje), não um dia exato de coleta. */
  ate?: boolean;
  rotulo: string;
  semana: string;
  pedidos: number;
  porEmpresa: { empresa: number; pedidos: number }[];
  aoFechar: () => void;
}): JSX.Element {
  const { centro, centroAtual, empresa } = useContexto();
  const navegar = useNavigate();

  /*
    A ONDA É DE UMA EMPRESA, E O PAINEL PODE SOMAR VÁRIAS.

    O CD 2201 junta a LUFT (22) e a TCL SEMP (23): a barra diz 337 e a onda da
    empresa ativa leva 244. Esconder isso faria alguém emitir e achar que
    limpou o dia. A empresa vem do CENTRO — é assim que a tela de separação
    resolve, e não dava para oferecer aqui um atalho que furasse essa regra.
  */
  const daAtiva = porEmpresa.find((e) => e.empresa === empresa)?.pedidos ?? 0;
  const dasOutras = porEmpresa.filter((e) => e.empresa !== empresa && e.pedidos > 0);

  const ir = (): void => {
    aoFechar();
    navegar(`/separacao?centro=${centro ?? ''}&${ate ? 'coletaAte' : 'coleta'}=${dia}`);
  };

  return (
    <Dialog.Root open onOpenChange={(v) => (v ? undefined : aoFechar())}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-900/45 backdrop-blur-[2px] data-[state=open]:animate-aparecer" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(520px,calc(100vw-24px))] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-borda bg-superficie shadow-forte data-[state=open]:animate-escalar">
          <div className="flex items-start justify-between gap-3 border-b border-borda bg-superficie2 px-5 py-3.5">
            <div>
              <Dialog.Title className="text-[16px] font-bold tracking-[-0.01em]">
                {ate ? 'Coleta vencida ou de hoje' : `Coleta de ${rotulo}`}
                {!ate && semana ? ` · ${semana}` : ''}
              </Dialog.Title>
              <Dialog.Description className="mt-0.5 text-xs text-textoFraco">
                {ate
                  ? 'Montar a onda de separação com tudo que tem prazo até hoje.'
                  : 'Montar a onda de separação já filtrada por este dia.'}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Botao variante="fantasma" tamanho="icone" aria-label="Fechar">
                <X className="h-5 w-5" />
              </Botao>
            </Dialog.Close>
          </div>

          <div className="space-y-3 px-5 py-4 text-[13px]">
            <p className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="leading-none">
                <b className="tabular text-[24px] font-bold text-primaria">{numero(pedidos)}</b>
                <span className="rotulo ml-1.5">agendados no ML</span>
              </span>
              {porEmpresa.length > 1 ? (
                <span className="text-[12px] text-textoFraco">
                  {porEmpresa.map((e) => `${e.pedidos} da ${e.empresa}`).join(' · ')}
                </span>
              ) : null}
            </p>

            {daAtiva === 0 ? (
              <div className="rounded-lg border border-status-atencao/30 bg-status-atencaoFundo px-3 py-2">
                <p className="font-semibold text-texto">
                  Nenhum destes pedidos é da empresa {empresa}.
                </p>
                <p className="mt-0.5 text-textoFraco">
                  A onda é montada por empresa, e a empresa vem do centro ativo. Troque o centro no
                  cabeçalho para separar os das outras.
                </p>
              </div>
            ) : (
              <ol className="grid gap-1 text-[12.5px] text-textoFraco">
                <li>
                  1. abre a Separação no centro{' '}
                  <b className="tabular text-texto">
                    {centroAtual?.centroEstoque ?? centro ?? '—'}
                  </b>{' '}
                  com a fila já recortada neste dia;
                </li>
                <li>
                  2. lá dá para <b className="text-texto">filtrar mais</b> — SKU, seção, UF — e
                  marcar o que entra;
                </li>
                <li>
                  3. nada é gravado no ERP até você{' '}
                  <b className="text-texto">emitir a onda</b> por lá.
                </li>
              </ol>
            )}

            {/*
              O número que vai aparecer lá pode ser MENOR, e dizer isso aqui
              custa uma linha. A regra de elegibilidade é a do Procfit: pedido
              com etiqueta já emitida em outra onda, com erro de integração ou
              em estado que não permite separação fica fora. Descobrir isso na
              tela seguinte, sem aviso, parece defeito.
            */}
            {daAtiva > 0 ? (
              <p className="text-[12px] text-textoFraco">
                Dos <b className="tabular text-texto">{numero(daAtiva)}</b> da empresa{' '}
                <b className="tabular text-texto">{empresa}</b>, a Separação vai mostrar os que o
                ERP libera — quem já está em outra onda ou com erro de integração fica de fora.
              </p>
            ) : null}

            {dasOutras.length > 0 ? (
              <p className="text-[12px] text-textoFraco">
                {dasOutras
                  .map((e) => `${numero(e.pedidos)} da empresa ${e.empresa}`)
                  .join(' e ')}{' '}
                {dasOutras.length === 1 && dasOutras[0]?.pedidos === 1 ? 'fica' : 'ficam'} de fora
                desta onda — troque o centro no cabeçalho para{' '}
                {dasOutras.length === 1 ? 'separá-los' : 'separá-las'}.
              </p>
            ) : null}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-borda bg-superficie2 px-5 py-3">
            <Dialog.Close asChild>
              <Botao variante="secundaria" tamanho="pequeno">
                Cancelar
              </Botao>
            </Dialog.Close>
            <Botao variante="primaria" tamanho="pequeno" disabled={daAtiva === 0} onClick={ir}>
              <Waves className="h-4 w-4" />
              Montar onda {daAtiva > 0 ? `de ${numero(daAtiva)} pedidos` : ''}
            </Botao>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Onde o dia está em relação ao que costuma ser a esta hora.
 *
 * A FAIXA aparece junto do número típico porque a referência tem largura: nas
 * quatro quintas medidas o faturamento a esta hora foi de R$ 222 mil a R$ 860
 * mil. Mostrar só "32% abaixo do típico" esconde que o típico varia mais que
 * isso — e alguém cobraria um turno por estar dentro da variação normal.
 */
function ComparacaoComReferencia({
  valor,
  referencia,
}: {
  valor: number;
  referencia: ReferenciaDoDia;
}): JSX.Element {
  const variacao = Math.round((valor / referencia.valorTipico - 1) * 1000) / 10;
  const abaixo = variacao < 0;
  /* Dentro da faixa observada não é atraso: é o dia normal sendo normal. */
  const dentroDaFaixa = valor >= referencia.valorMinimo && valor <= referencia.valorMaximo;

  return (
    <p
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]"
      title={`Nos ${referencia.dias} dias iguais anteriores, até esta hora, o faturamento foi de ${moeda(
        referencia.valorMinimo,
      )} a ${moeda(referencia.valorMaximo)}.`}
    >
      <span
        className={cn(
          'tabular inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-bold',
          dentroDaFaixa
            ? 'bg-superficie3 text-textoFraco'
            : abaixo
              ? 'bg-status-atencaoFundo text-status-atencao'
              : 'bg-status-livreFundo text-status-livre',
        )}
      >
        {abaixo ? <ArrowDown className="h-3.5 w-3.5" /> : <ArrowUpRight className="h-3.5 w-3.5" />}
        {percentual(Math.abs(variacao))}
      </span>
      <span className="text-textoFraco">
        {abaixo ? 'abaixo' : 'acima'} do típico desta hora —{' '}
        <b className="tabular text-texto">{moedaCompacta(referencia.valorTipico)}</b> nos últimos{' '}
        {referencia.dias} dias iguais
        {dentroDaFaixa ? ', dentro da variação normal' : ''}.
      </span>
    </p>
  );
}
